import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { 
  Box, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText,
  Badge,
  CircularProgress
} from '@mui/material';
import {
  Home as HomeIcon,
  AdminPanelSettings as AdminIcon,
  CalendarToday as CalendarIcon,
  Group as GroupIcon,
  Person as ProfileIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Settings as SettingsIcon,
  ImportExport as ImportExportIcon,
  Category as CategoryIcon,
  AccountCircle as AccountIcon,
  BarChart as BarChartIcon,
  School as SchoolIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import axiosInstance from '../utils/axiosInstance';

const SiteNavBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const [refreshingReports, setRefreshingReports] = useState(false);
  
  const isSystemAdmin = user?.role === 'admin';
  const isOrgAdmin = user?.role === 'org_admin';
  const isInstructor = user?.role === 'instructor';
  const isSuperAdmin = user?.role === 'superadmin';
  const isAdminLevel = isSystemAdmin || isOrgAdmin || isInstructor || isSuperAdmin;

  const getNavItems = () => {
    const commonItems = [
      { 
        text: 'Calendar', 
        icon: <CalendarIcon />, 
        path: '/calendar'
      },
      { 
        text: 'My Profile', 
        icon: <AccountIcon />, 
        path: '/profile'
      }
    ];

    const adminItems = [
      {
        text: 'Course Management',
        icon: <SchoolIcon />,
        path: '/admin/courses'
      },
      {
        text: 'Group',
        icon: <GroupIcon />,
        path: '/admin/groups'
      },
      {
        text: 'Reports',
        icon: <BarChartIcon />,
        path: '/admin/reports'
      }
    ];

    // Items specific to system admin and org admin
    const organizationItems = [
      { 
        text: 'Users', 
        icon: <ProfileIcon />, 
        path: '/admin/users',
        showFor: ['admin', 'org_admin']
      },
    ];

    // System admin only items
    const systemAdminItems = [
      {
        text: 'Organizations',
        icon: <BusinessIcon />,
        path: '/admin/organizations',
        showFor: ['admin', 'superadmin']
      },
      {
        text: 'Subdomain Management',
        icon: <BusinessIcon />,
        path: '/admin/subdomain-management',
        showFor: ['superadmin']
      },
      {
        text: 'System Settings',
        icon: <SettingsIcon />,
        path: '/admin/settings',
        showFor: ['admin', 'superadmin']
      },
      {
        text: 'Import/Export',
        icon: <ImportExportIcon />,
        path: '/admin/import-export',
        showFor: ['admin', 'superadmin']
      }
    ];
    
    let items = [...commonItems];

    if (isAdminLevel) {
      items = [...items, ...adminItems];
      
      // Add organization items if system admin or org admin
      if (isSystemAdmin || isOrgAdmin) {
        items = [...items, ...organizationItems.filter(item => item.showFor.includes(user.role))];
      }
      
      // Add system admin items if system admin or superadmin
      if (isSystemAdmin || isSuperAdmin) {
        items = [...items, ...systemAdminItems.filter(item => item.showFor.includes(user.role))];
      }
    }

    return items;
  };

  const navItems = getNavItems();

  const handleNavigation = async (path) => {
    // If navigating to reports, refresh materialized views first
    if (path === '/admin/reports' && isAdminLevel) {
      try {
        setRefreshingReports(true);
        // Call the API to refresh report data
        await axiosInstance.post('/api/reports/refresh');
        console.log('Successfully refreshed report data');
      } catch (error) {
        console.error('Error refreshing report data:', error);
        // Continue navigation even if refresh fails
      } finally {
        setRefreshingReports(false);
      }
    }
    
    // Navigate to the requested path
    navigate(path);
  };

  return (
    <Box
  sx={{
    width: 240, // Changed from 260px to match LayoutManager
    bgcolor: '#0a64bf',
    borderRight: 1,
    borderColor: 'rgba(255, 255, 255, 0.12)',
    height: '100%', 
    position: 'fixed',
    left: 0,
    top: 64,
    bottom: 0,
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '1px 0px 4px rgba(0, 0, 0, 0.2)',
    overflowY: 'auto'
  }}
>
      <List
        sx={{ 
          width: '100%',
          py: 0, // Removed top/bottom padding
          m: 0,  // Remove any margins
          '& .MuiListItem-root': {
            px: 2,
            py: 1,
            cursor: 'pointer',
            mx: 'auto',
            width: '90%',
            mb: 0.5,
            borderRadius: 1,
            transition: 'background-color 0.2s',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.12)',
            },
          },
          '& .MuiListItem-root.active': {
            bgcolor: 'rgba(255, 255, 255, 0.08)',
          },
          '& .MuiListItemIcon-root': {
            minWidth: 40,
            color: 'white',
            '& .MuiSvgIcon-root': {
              fontSize: '1.5rem'
            }
          },
          '& .MuiListItemText-primary': {
            color: 'white',
            fontSize: '0.9rem'
          }
        }}
      >
        {navItems.map((item) => {
          const isActive = location.pathname.startsWith(item.path);
          const isReportsItem = item.path === '/admin/reports';
          
          return (
            <ListItem
              key={item.text}
              className={isActive ? 'active' : ''}
              onClick={() => handleNavigation(item.path)}
              disabled={isReportsItem && refreshingReports}
            >
              <ListItemIcon>
                {isReportsItem && refreshingReports ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  item.icon
                )}
              </ListItemIcon>
              <ListItemText 
                primary={isReportsItem && refreshingReports ? 'Refreshing Reports...' : item.text}
                primaryTypographyProps={{
                  sx: { fontWeight: isActive ? 500 : 400 }
                }}
              />
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
};

export default SiteNavBar;